import { apiRequest } from "@/lib/api";

export type CompanyProfile = {
  id: string;
  name: string;
  ownerFullName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  companyType?: string | null;
  companySize?: string | null;
  currentPlan: "starter" | "pro" | "team";
  createdAt: string;
  updatedAt: string;
  planEntitlements: {
    tickets: {
      max: number | null;
      used: number;
      limitReached: boolean;
    };
  };
};

export type CompanySettings = {
  id: string;
  companyId: string;
  companyAddress?: string | null;
  companyLogoPath?: string | null;
  companyLogoUrl?: string | null;
  companySealSignaturePath?: string | null;
  companySealSignatureUrl?: string | null;
  pdfBrandTagline?: string | null;
  pdfBrandColorHex?: string | null;
  currencyCode: string;
  changeOrderEnabled: boolean;
  tmTicketEnabled: boolean;
  extraWorkEnabled: boolean;
  ticketTypeOrder: Array<"co" | "tm" | "ew">;
  coPrefix: string;
  tmPrefix: string;
  extraWorkPrefix: string;
  coNumberFormat: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  tmNumberFormat: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  ewNumberFormat: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  coNextNumber: number;
  tmNextNumber: number;
  ewNextNumber: number;
  defaultReminderCadenceDays: number;
  reminderDayOffsets: number[];
  requireRejectionComment: boolean;
  allowManualApprovalOverride: boolean;
  requireSignatureToApprove: boolean;
  autoDeletePendingApprovalEnabled: boolean;
  autoDeletePendingApprovalDays: number;
  notifyTicketApproved: boolean;
  notifyTicketRejected: boolean;
  notifySignatureCaptured: boolean;
  notifyReminderSent: boolean;
  notifyPaymentFollowupSent: boolean;
  notifyPaymentRecorded: boolean;
  notifyWeeklySummary: boolean;
  scheduleAutoStartEnabled: boolean;
  scheduleAutoCompleteEnabled: boolean;
  scheduleWorkdayStartTime: string;
  scheduleWorkdayEndTime: string;
  approvalLinkExpiryDays: number;
  defaultTaxRate: number;
  twoFactorEnabled: boolean;
  isInitialized: boolean;
  projectSetupSkipped: boolean;
};

type UpdateCompanySettingsPayload = {
  companyAddress?: string;
  companyLogoDataUrl?: string;
  companySealSignatureDataUrl?: string;
  removeCompanyLogo?: boolean;
  removeCompanySealSignature?: boolean;
  pdfBrandTagline?: string;
  pdfBrandColorHex?: string;
  currencyCode?: string;
  changeOrderEnabled?: boolean;
  tmTicketEnabled?: boolean;
  extraWorkEnabled?: boolean;
  ticketTypeOrder?: Array<"co" | "tm" | "ew">;
  coPrefix?: string;
  tmPrefix?: string;
  extraWorkPrefix?: string;
  coNumberFormat?: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  tmNumberFormat?: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  ewNumberFormat?: "PREFIX-YEAR-SEQ" | "PREFIX-SEQ";
  coNextNumber?: number;
  tmNextNumber?: number;
  ewNextNumber?: number;
  defaultReminderCadenceDays?: number;
  reminderDayOffsets?: number[];
  requireRejectionComment?: boolean;
  allowManualApprovalOverride?: boolean;
  requireSignatureToApprove?: boolean;
  autoDeletePendingApprovalEnabled?: boolean;
  autoDeletePendingApprovalDays?: number;
  notifyTicketApproved?: boolean;
  notifyTicketRejected?: boolean;
  notifySignatureCaptured?: boolean;
  notifyReminderSent?: boolean;
  notifyPaymentFollowupSent?: boolean;
  notifyPaymentRecorded?: boolean;
  notifyWeeklySummary?: boolean;
  scheduleAutoStartEnabled?: boolean;
  scheduleAutoCompleteEnabled?: boolean;
  scheduleWorkdayStartTime?: string;
  scheduleWorkdayEndTime?: string;
  approvalLinkExpiryDays?: number;
  defaultTaxRate?: number;
  twoFactorEnabled?: boolean;
  isInitialized?: boolean;
  projectSetupSkipped?: boolean;
};

export function fetchCompanySettings() {
  return apiRequest<CompanySettings>("/companies/me/settings", { method: "GET" });
}

export function fetchCompanyProfile() {
  return apiRequest<CompanyProfile>("/companies/me", { method: "GET" });
}

export function updateCompanySettings(payload: UpdateCompanySettingsPayload) {
  return apiRequest<CompanySettings>("/companies/me/settings", {
    method: "PATCH",
    body: payload,
  });
}
